import { useEffect, useRef, useState } from "react";
import { ColorBox, DataGrid, TagBox, TextArea } from "devextreme-react";
import {
  Button,
  Column,
  CustomRule,
  Editing,
  Item,
  Lookup,
  RangeRule,
  RequiredRule,
  RowDragging,
  Toolbar,
} from "devextreme-react/data-grid";
import { alert, confirm } from "devextreme/ui/dialog";
import validationEngine from "devextreme/ui/validation_engine";
import ArrayStore from "devextreme/data/array_store";
import axios from "axios";
import styled from "styled-components";
import { toastMsg } from "../MonthlyReport/utils";
import { NY } from "../SalesManagement/Sales/data";

const DataGridWrapper = styled.div`
  .dx-data-row {
    white-space: pre-wrap;
  }
  .dx-cell-modified:not(.dx-datagrid-invalid) {
    background-color: transparent !important;
  }
  .dx-colorbox
    .dx-colorbox-input-container
    .dx-dropdowneditor-input-wrapper
    .dx-colorbox-input {
    font-size: 1em;
  }
  .dx-button {
    margin: 0;
  }
  > div {
    &[id$="MappingGrid"] {
      max-width: 99vw;
    }
  }
`;

const ConfigureMappingData = ({
  cat,
  colorEnabled,
  configColumnData,
  sortOrderEnabled,
  name,
  sumFieldEnabled,
  issRelEnabled,
}) => {
  const [configInfo, setConfigInfo] = useState(null);
  const [isEditMode, setEditMode] = useState(false);
  const [newConfigData, setNewConfigData] = useState(null);
  const [oldConfigData, setOldConfigData] = useState(null);

  const configureRef = useRef(null);

  const handleSaveCodeData = (data) => {
    setConfigInfo({ codeListId: data.codeListId, date: data.date });
    setOldConfigData(new ArrayStore({ key: "codeId", data: data.code }));
  };

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BECCO_API_URL}/api/common/codeList?name=${name}&cat=${cat}`
      )
      .then((res) => {
        const data = res.data;
        handleSaveCodeData(data);
      });
  }, []);

  const convertToCamelCase = (str) => {
    return str
      .split(" ")
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        } else {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
      })
      .join("");
  };

  const handleAddRow = () => {
    //* row 추가 버튼을 누르면 dataSource에 추가되고, 각 셀에 대한 validation을 진행한다.
    const newData = {
      [configColumnData.dataField]: null,
      color: colorEnabled ? "#ffffff" : null,
      sortOrder: null,
      descr: null,
      prevCodeId: [],
    };

    const component = configureRef.current.instance;
    component
      .getDataSource()
      .store()
      .push([{ type: "insert", data: newData, index: 0 }]);

    setTimeout(() => {
      // row의 validation.
      for (let dataField of Object.keys(newData)) {
        const value = component.cellValue(0, dataField);
        component.cellValue(0, dataField, value);
      }
    }, 200);
  };

  const handleCellStyle = (e) => {
    if (e.rowType === "header") {
      e.cellElement.style.textAlign = "center";
      e.cellElement.style.fontWeight = "bold";
    }
  };

  const handleClickEditMode = () => {
    if (isEditMode) {
      setEditMode(false);
      setNewConfigData(null);
    } else {
      setEditMode(true);
      const modifiedData = oldConfigData._array.map((el) => ({
        ...el,
        prevCodeId: [el.codeId],
        isOldValue: true,
      }));

      setNewConfigData(new ArrayStore({ key: "codeId", data: modifiedData }));
    }
  };

  const handleClickSave = async () => {
    const validate = validateGridValue() && validateMappingPrevIds();

    if (validate) {
      const result = await confirm(
        "변경된 사항은 소급 적용되며 되돌릴 수 없습니다.<br/>저장하시겠습니까?",
        "Save Confirmation"
      );
      if (result) {
        const code = configureRef.current.instance
          .getVisibleRows()
          .map(({ data }) => {
            const { codeId, ...rest } = data;
            return rest;
          });

        const body = { name, cat, prevCodeListId: configInfo.codeListId, code };
        axios
          .post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/common/codeList`,
            body
          )
          .then((res) => {
            handleSaveCodeData(res.data);
            toastMsg(`${name}가 정상적으로 수정되었습니다`, true);
          })
          .then(() => setEditMode(!isEditMode))
          .catch((err) => {
            toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
            throw err;
          });
      }
    }
  };

  const handleDeleteRow = (e) => {
    // * row 삭제 시 prevCodeId에 맵핑된 value를 disable = false로 바꾼다.
    const prevCodeArr = e.row.data.prevCodeId;
    prevCodeArr.forEach((code) => {
      const tagData = oldConfigData._array.find((el) => el.codeId === code);
      tagData.disabled = false;
    });

    //* dataSource에서 해당 row를 삭제한다.
    const row = e.row;
    if (!row.data.codeId) {
      e.component.deleteRow(row.rowIndex);
    } else {
      e.component
        .getDataSource()
        .store()
        .push([{ type: "remove", key: row.data.codeId }]);
    }
  };

  const handleDisableEdit = (e) => {
    // * validation이 통과된 후, code name에 해당하는 cell의 수정을 막는다. (첫번째 column)
    if (e.dataField === configColumnData.dataField) {
      if (e.row.data.isOldValue) {
        e.editorOptions.disabled = true;
      } else {
        const cellValue = e.value;
        const validationRules = e.validationRules;
        const validationResult = validationEngine.validate(
          cellValue,
          validationRules
        );
        e.editorOptions.disabled = validationResult.isValid;
      }
    }
  };

  const handleMappingOriginalData = (e) => {
    // * 새 row의 name이 기존에 있었던 name이고 다른 row에 맵핑되지 않았다면 기존 data를 맵핑해준다.
    if (
      isEditMode &&
      e.prevColumnIndex ===
        e.component.getVisibleColumnIndex(configColumnData.dataField)
    ) {
      setTimeout(() => {
        const data = e.rows[e.prevRowIndex].data;
        const tag = oldConfigData._array.find(
          (el) =>
            el[configColumnData.dataField] === data[configColumnData.dataField]
        );
        const mappingColumnIndex =
          e.component.getVisibleColumnIndex("prevCodeId");
        if (tag && !tag.disabled) {
          const mappedTags = e.component.cellValue(
            e.prevRowIndex,
            mappingColumnIndex
          );
          e.component.cellValue(e.prevRowIndex, mappingColumnIndex, [
            ...mappedTags,
            tag.codeId,
          ]);
        }
      }, 200); // cell value가 입력되기까지 시간이 걸림.
    }
  };

  const handleOnReorder = (e) => {
    const newData = e.itemData;
    if (!newData.codeId) {
      // codeId가 없다면 deleteRow
      e.component.deleteRow(e.fromIndex);
    } else {
      // 있다면 dataSource에서 remove
      e.component
        .getDataSource()
        .store()
        .push([{ type: "remove", key: newData.codeId }]);
    }
    e.component
      .getDataSource()
      .store()
      .push([{ type: "insert", data: newData, index: e.toIndex }]);

    setTimeout(() => {
      // reorder된 row의 validation.
      for (let dataField of Object.keys(e.itemData)) {
        const value = e.component.cellValue(e.toIndex, dataField);
        e.component.cellValue(e.toIndex, dataField, value);
      }
    }, 200);
  };

  const renderColorBox = (e) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0px 10px",
          width: "100%",
        }}
      >
        <span
          style={{
            border: "0.2px solid #e0e0e0",
            backgroundColor: `${e.value}`,
            display: "inline-block",
            height: "16px",
            width: "16px",
          }}
        />{" "}
        {e.value?.toUpperCase()}
      </div>
    );
  };

  const renderConfigTitle = () => {
    return (
      <>
        <span style={{ fontSize: "16px", fontWeight: "bold" }}>{name}</span>
        {!isEditMode && (
          <span style={{ fontSize: "16px" }}> (V. {configInfo?.date})</span>
        )}
      </>
    );
  };

  const renderEditColorBox = (cell) => {
    return (
      <ColorBox
        defaultValue={cell?.value?.toUpperCase() || "#ffffff"}
        onValueChanged={(e) => {
          cell.setValue(e.value?.toUpperCase());
        }}
      />
    );
  };

  const renderEditTagBox = (cell) => {
    return (
      <TagBox
        style={{ padding: "5px 10px 2px 10px" }}
        dataSource={oldConfigData}
        defaultValue={cell.value}
        displayExpr={configColumnData.dataField}
        valueExpr="codeId"
        hideSelectedItems={true}
        itemRender={renderTagBoxDropDown}
        showSelectionControls={true}
        onClosed={(e) => {
          e.component.repaint();
        }}
        onValueChanged={(e) => {
          cell.setValue(e.value);
        }}
        onSelectionChanged={(e) => {
          e.addedItems.forEach((item) => {
            oldConfigData._array.forEach((data) => {
              if (data.codeId === item.codeId) {
                data.disabled = true;
                e.component.repaint();
              }
            });
          });
          e.removedItems.forEach((item) => {
            oldConfigData._array.forEach((data) => {
              if (data.codeId === item.codeId) {
                data.disabled = false;
                e.component.repaint();
              }
            });
          });
        }}
      />
    );
  };

  const renderEditTextArea = (cell) => {
    return (
      <TextArea
        autoResizeEnabled={true}
        defaultValue={cell.value}
        inputAttr={{ style: "line-height: 1.5em; padding: 0.5em;" }}
        minHeight={20}
        onInitialized={(e) => {
          // * textarea auto-resizing
          setTimeout(() => {
            e.component.repaint();
          }, 0);
        }}
        onKeyDown={(e) => {
          // * new line with "Enter" key. update Cell size.
          if (e.event.key === "Enter") {
            e.event.stopPropagation();
          }
          cell.component.updateDimensions();
        }}
        onValueChanged={(e) => {
          cell.setValue(e.value);
        }}
      />
    );
  };

  const renderTagBoxDropDown = (tag) => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            fontWeight: "bold",
            marginRight: "20px",
            display: "inline-block",
          }}
        >
          {tag[configColumnData.dataField]}
        </div>
        <div
          style={{
            height: "auto",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {tag.descr}
        </div>
      </div>
    );
  };

  const validateGridValue = () => {
    const gridInstance = configureRef.current.instance;
    const validationState =
      gridInstance.getController("validating")._validationState;
    const errorMessages = [];

    const visibleRowKeys = gridInstance.getVisibleRows().map((row) => row.key);

    validationState.forEach((row) => {
      if (visibleRowKeys.includes(row.key) && row.validationResults) {
        const cellValidation = Object.values(row.validationResults);
        cellValidation.forEach((c) => {
          if (c.status === "invalid") {
            const rule = c.brokenRule;
            const message =
              rule.type === "required"
                ? `${c.name}은 필수 항목입니다`
                : rule.message;
            errorMessages.push(message);
          }
        });
      }
    });

    if (errorMessages.length > 0) {
      alert(errorMessages.join("<br />"), "Validation Error");
      return false;
    }
    return true;
  };

  const validateDuplicateValue = (e, dataField) => {
    const columnValueArr = configureRef.current.instance
      .getVisibleRows()
      .map((el) => el.data[dataField]);
    return columnValueArr.filter((el) => el === e.value).length <= 1;
  };

  const validateMappingPrevIds = () => {
    const mappedPrevCodeIds = configureRef.current.instance
      .getVisibleRows()
      .flatMap((ele) => ele.data.prevCodeId);
    const unmappedCodes = oldConfigData._array
      .filter((el) => !mappedPrevCodeIds.includes(el.codeId))
      .map((el) => el[configColumnData.dataField]);

    if (unmappedCodes.length > 0) {
      const errorMessage = `기존의 ${
        configColumnData.caption
      } 중 “${unmappedCodes.join(", ")}”이(가) 맵핑되지 않았습니다`;
      alert(errorMessage, "Mapping Validation Error");
      return false;
    }
    return true;
  };

  return (
    <DataGridWrapper>
      <DataGrid
        allowColumnResizing={true}
        columnResizingMode={"nextColumn"}
        dataSource={isEditMode ? newConfigData : oldConfigData}
        id={`${convertToCamelCase(name)}MappingGrid`}
        onCellPrepared={handleCellStyle}
        // onEditorPreparing={handleDisableEdit}
        onFocusedCellChanging={handleMappingOriginalData}
        // onInitNewRow={handleInitNewRow}
        ref={configureRef}
        showBorders={true}
        showColumnLines={true}
        width={"100%"}
        wordWrapEnabled={true}
      >
        <Editing
          allowAdding={isEditMode}
          allowDeleting={isEditMode}
          allowUpdating={isEditMode}
          mode="batch"
        />
        <RowDragging allowReordering={isEditMode} onReorder={handleOnReorder} />
        <Toolbar>
          <Item name="gridTitle" location="before" render={renderConfigTitle} />
          <Item
            name="editButton"
            location="after"
            options={{
              icon: "edit",
              onClick: handleClickEditMode,
            }}
            visible={!isEditMode}
            widget="dxButton"
          />
          <Item
            name="addRowBtn"
            location="after"
            options={{ icon: "add", onClick: handleAddRow }}
            visible={isEditMode}
            widget="dxButton"
          />
          <Item
            name="saveBtn"
            location="after"
            options={{
              icon: "save",
              onClick: handleClickSave,
            }}
            visible={isEditMode}
            widget="dxButton"
          />
          <Item
            name="revertBtn"
            location="after"
            options={{
              icon: "revert",
              onClick: handleClickEditMode,
            }}
            visible={isEditMode}
            widget="dxButton"
          />
        </Toolbar>
        <Column {...configColumnData}>
          <RequiredRule message="" />
          {configColumnData.isNumber && (
            <RangeRule
              min={0}
              message={`${name} ${configColumnData.caption}은 양수여야 합니다`}
            />
          )}
          <CustomRule
            message={`${name} ${configColumnData.caption}은 중복될 수 없습니다`}
            validationCallback={(e) =>
              validateDuplicateValue(e, configColumnData.dataField)
            }
          />
        </Column>
        {colorEnabled && (
          <Column
            alignment="center"
            caption="Color"
            dataField="color"
            dataType="string"
            cellRender={renderColorBox}
            editCellRender={renderEditColorBox}
            showEditorAlways={isEditMode}
            width={150}
          />
        )}
        {sortOrderEnabled && (
          <Column
            alignment="center"
            caption="Sort Order"
            dataField="sortOrder"
            dataType="number"
            showEditorAlways={isEditMode}
            width={120}
          >
            <RequiredRule message="" />
            <RangeRule min={0} message={"Sort Order는 양수여야 합니다"} />
            <CustomRule
              message={"Sort Order는 중복될 수 없습니다"}
              validationCallback={(e) => validateDuplicateValue(e, "sortOrder")}
            />
          </Column>
        )}
        {sumFieldEnabled && (
          <Column
            dataField={"sumField"}
            alignment={"center"}
            caption={"Sum Field"}
            dataType={"string"}
            showEditorAlways={isEditMode}
            width={120}
          >
            <Lookup dataSource={NY} displayExpr={"name"} valueExpr={"id"} />
          </Column>
        )}
        {issRelEnabled && (
          <Column
            dataField={"issRel"}
            alignment={"center"}
            caption={"Issue-related"}
            dataType={"string"}
            showEditorAlways={isEditMode}
            width={120}
          >
            <Lookup dataSource={NY} displayExpr={"name"} valueExpr={"id"} />
          </Column>
        )}
        <Column
          alignment="left"
          caption="Description"
          dataField="descr"
          dataType="string"
          editCellRender={renderEditTextArea}
          showEditorAlways={isEditMode}
        >
          <RequiredRule message="" />
        </Column>
        <Column
          alignment="center"
          caption="Mapping Previous Value"
          dataField="prevCodeId"
          dataType="object"
          editCellRender={renderEditTagBox}
          showEditorAlways={true}
          visible={isEditMode}
        />
        <Column type="buttons" visible={isEditMode}>
          <Button icon="trash" onClick={handleDeleteRow} />
        </Column>
      </DataGrid>
    </DataGridWrapper>
  );
};

export default ConfigureMappingData;
